import React, { ReactElement, useState } from "react";
import { Body, SectionHeader } from "@sprout/typography";
import Container from "@components/ui/container";
import { Checkbox } from "@sprout/checkbox";
import Layout from "@components/layout";
import useTranslation from "@hooks/use_translation";
import { useShowFlash } from "@sprout/flash.tsx";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";
import ErrorIcon from "@sprout/icons/error.tsx";
import { router } from "@inertiajs/react";

const CATEGORIES = ["job_alerts", "dream_jobs"] as const;
type Category = (typeof CATEGORIES)[number];

interface EmailPreferencesProps {
  token: string;
  email: string;
  preferences: Record<string, boolean>;
  updatePath: string;
  enableEmailsPath: string;
  suppressed: boolean;
  publicFacing: boolean;
}

const EmailPreferences = ({
  token,
  preferences,
  updatePath,
  enableEmailsPath,
  suppressed: initialSuppressed,
  publicFacing,
}: EmailPreferencesProps) => {
  const { t } = useTranslation("unsubscribe");
  const showFlash = useShowFlash();
  const [suppressed, setSuppressed] = useState(initialSuppressed);
  const [enabling, setEnabling] = useState(false);

  const handleEnableEmails = async () => {
    setEnabling(true);
    try {
      await axios.delete(enableEmailsPath, { data: { token } });
      setSuppressed(false);
      showFlash(t("suppressed.success"), "success");
    } catch {
      showFlash(t("error"), "error");
    } finally {
      setEnabling(false);
    }
  };

  const initialValues = Object.fromEntries(
    CATEGORIES.map((category) => [category, preferences[`unsubscribe_${category}`]]),
  ) as Record<Category, boolean>;

  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const allChecked = CATEGORIES.every((category) => values[category]);

  const handleCategoryChange = (category: Category) => {
    setValues((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleAllChange = () => {
    const newValue = !allChecked;
    setValues(
      Object.fromEntries(CATEGORIES.map((category) => [category, newValue])) as Record<
        Category,
        boolean
      >,
    );
  };

  const handleCancel = () => window.history.back();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        CATEGORIES.map((category) => [`unsubscribe_${category}`, values[category]]),
      );
      await axios.patch(updatePath, {
        token,
        email_preference: { ...payload, unsubscribe_all: allChecked },
      });

      await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });

      showFlash(t("success"), "success");

      publicFacing
        ? (window.location.href = `/unsubscribe/confirmation`)
        : router.visit("/profile");
    } catch {
      showFlash(t("error"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <SectionHeader as="h1">{t("title")}</SectionHeader>
      {suppressed && (
        <div className="flex items-center justify-between gap-4 px-5 py-4 rounded-lg bg-[var(--yellow-e25)] mb-6">
          <div className="flex items-start gap-3">
            <div className="shrink-0 pt-1">
              <ErrorIcon color="yellow-warning" size="md" />
            </div>
            <div className="flex flex-col gap-1">
              <Body medium>{t("suppressed.title")}</Body>
              <Body>{t("suppressed.body")}</Body>
            </div>
          </div>
          <SecondaryButton onClick={handleEnableEmails} disabled={enabling}>
            {t("suppressed.enableEmails")}
          </SecondaryButton>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col gap-3">
          {CATEGORIES.map((category) => (
            <Checkbox
              key={category}
              id={`unsubscribe-${category.replace(/_/g, "-")}`}
              options={[{ label: t(category), value: category }]}
              values={values[category] ? [category] : []}
              onSelect={() => handleCategoryChange(category)}
              isMulti
            />
          ))}
          <Checkbox
            id="unsubscribe-all"
            options={[{ label: t("unsubscribe_all"), value: "all" }]}
            values={allChecked ? ["all"] : []}
            onSelect={handleAllChange}
            isMulti
          />
          <div className="flex gap-3 pt-6">
            <PrimaryButton type="submit" disabled={submitting}>
              {t("save")}
            </PrimaryButton>
            <SecondaryButton disabled={submitting} onClick={handleCancel}>
              {t("cancel")}
            </SecondaryButton>
          </div>
        </div>
      </form>
    </Container>
  );
};

EmailPreferences.layout = (page: ReactElement<EmailPreferencesProps>) => (
  <Layout publicFacing={page.props.publicFacing}>{page}</Layout>
);
export default EmailPreferences;
